/**
 * Mixpanel client-side tracking utility.
 *
 * Wraps the global `window.mixpanel` stub (loaded via index.html snippet).
 * The webapp adapter over the one event registry in `@a7y/telemetry`
 * (A7Y-777): `mpTrack` accepts only a declared `MixpanelEventName`; import the
 * `MixpanelEvent` constants from `@a7y/telemetry`.
 *
 * Identity strategy:
 *   - Anonymous visitors get an auto-assigned distinct_id
 *   - Marketing signup intent: identify(email)
 *   - Authenticated users: identify(userId) + people.set
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal subset of the Mixpanel JS SDK we actually use. */
interface MixpanelInstance {
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string) => void;
  alias: (alias: string, distinctId?: string) => void;
  reset: () => void;
  opt_in_tracking?: (options?: { track: boolean }) => void;
  opt_out_tracking?: (options?: { delete_user: boolean }) => void;
  stop_session_recording?: () => void;
  start_session_recording?: () => void;
  people: {
    set: (properties: Record<string, unknown>) => void;
    set_once: (properties: Record<string, unknown>) => void;
  };
  register: (properties: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    mixpanel?: MixpanelInstance;
  }
}

// ---------------------------------------------------------------------------
// Core helpers
// ---------------------------------------------------------------------------

import type { MixpanelEventName } from '@a7y/telemetry';
import { hasAnalyticsConsent } from './consent';
import { isInternalAnalyticsEmail, isE2eTestTrafficEmail } from '@a7y/types';

function mp(): MixpanelInstance | null {
  if (!hasAnalyticsConsent()) return null;
  return window.mixpanel ?? null;
}

/**
 * Internal accounts are suppressed from analytics UNLESS they are the
 * scripted e2e signup traffic (A7Y-3634 pattern), which must be flagged
 * rather than dropped so the onboarding funnel can be probed end to end.
 */
function isSuppressedAnalyticsEmail(email: string | null | undefined): boolean {
  return isInternalAnalyticsEmail(email) && !isE2eTestTrafficEmail(email);
}

/** Tracks the current user's email for internal-account suppression. */
let _currentUserEmail: string | null = null;

/** Track an event with optional properties. */
export function mpTrack(event: MixpanelEventName, properties?: Record<string, unknown>) {
  if (isSuppressedAnalyticsEmail(_currentUserEmail)) return;
  mp()?.track(event, properties);
}

/**
 * Identify a known user (e.g. after Clerk auth).
 * Sets people properties for Mixpanel Profiles.
 */
export function mpIdentify(
  userId: string,
  traits?: {
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    orgId?: string;
    plan?: string;
  }
) {
  _currentUserEmail = traits?.email ?? null;
  if (isSuppressedAnalyticsEmail(_currentUserEmail)) return;

  const m = mp();
  if (!m) return;

  if (isE2eTestTrafficEmail(_currentUserEmail)) {
    m.register({ is_test_traffic: true });
  }

  // A7Y-707: prefix with `uid-` so client-side distinct_ids match the
  // server-side convention (`uid-`/`org-`/`anon-`).
  m.identify(`uid-${userId}`);

  if (traits) {
    const peopleProps: Record<string, unknown> = {};
    if (traits.email) peopleProps.$email = traits.email;
    if (traits.name) peopleProps.$name = traits.name;
    if (traits.firstName) peopleProps.$first_name = traits.firstName;
    if (traits.lastName) peopleProps.$last_name = traits.lastName;
    if (traits.orgId) peopleProps.org_id = traits.orgId;
    if (traits.plan) peopleProps.plan = traits.plan;
    m.people.set(peopleProps);
  }
}

/**
 * Identify by email (for anonymous → known stitching on signup intent).
 */
export function mpIdentifyByEmail(
  email: string,
  traits?: { firstName?: string; lastName?: string }
) {
  _currentUserEmail = email;
  if (isSuppressedAnalyticsEmail(email)) return;

  const m = mp();
  if (!m) return;

  if (isE2eTestTrafficEmail(email)) {
    m.register({ is_test_traffic: true });
  }

  m.identify(email);
  const peopleProps: Record<string, unknown> = { $email: email };
  if (traits?.firstName) peopleProps.$first_name = traits.firstName;
  if (traits?.lastName) peopleProps.$last_name = traits.lastName;
  m.people.set(peopleProps);
}

/**
 * Alias a canonical id to the visitor's current (or supplied) distinct_id.
 * Used by FinishSignup to stitch anon/email distinct_ids to `uid-${clerkUser.id}`.
 */
export function mpAlias(alias: string, originalDistinctId?: string) {
  if (isSuppressedAnalyticsEmail(_currentUserEmail)) return;
  mp()?.alias(alias, originalDistinctId);
}

/** Reset identity on logout. */
export function mpReset() {
  _currentUserEmail = null;
  mp()?.reset();
}

/**
 * Identify a user, retrying if the Mixpanel SDK hasn't loaded yet.
 * Handles the race between consent-gated SDK loading and Clerk auth.
 */
const IDENTIFY_RETRY_INTERVALS = [500, 1000, 2000, 4000];

export function mpIdentifyWhenReady(
  userId: string,
  traits?: {
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    orgId?: string;
    plan?: string;
  }
) {
  _currentUserEmail = traits?.email ?? null;
  if (isSuppressedAnalyticsEmail(_currentUserEmail)) return;

  if (mp()) {
    mpIdentify(userId, traits);
    return;
  }

  let attempt = 0;
  const retry = () => {
    if (attempt >= IDENTIFY_RETRY_INTERVALS.length) return;
    const delay = IDENTIFY_RETRY_INTERVALS[attempt];
    attempt++;
    setTimeout(() => {
      if (mp()) {
        mpIdentify(userId, traits);
      } else {
        retry();
      }
    }, delay);
  };
  retry();
}

/** Set super properties that persist across all subsequent events. */
export function mpRegister(properties: Record<string, unknown>) {
  mp()?.register(properties);
}
